import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import PublicIcon from '@mui/icons-material/Public';

import { Add, Delete, Search, UploadFile } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { setReplacementsModal, updateReplacements } from '../../redux/features/app/app';
import { ReplacementsModalType } from '../../redux/store/IStore';
import {
  createReplacementSet,
  createReplacementSetItems,
  deleteReplacementSet,
  getReplacementSetItems,
  getReplacementSets,
  IEntryValue,
  IReplacementSet,
  LocalReplacementSets,
  patchReplacementSet,
  ReplacementSetItems,
  ReplacementSetType,
  updateReplacementSetItems,
} from '../../api/ReplacementsService';
import { enqueueSnackbar } from 'notistack';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';

const priorities = {
  PERSONAL: 0,
  SHARED: 1,
  GLOBAL: 2,
};

const ReplacementsModal = () => {
  const dispatch = useAppDispatch();
  const { visible: replacementsModalVisible, type: replacementModalType } = useAppSelector(
    (state) => state.app.replacementsModal
  );

  const [replacementSetsFilter, setReplacementSetsFilter] = useState<string>('');
  const updateReplacementSetFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setReplacementSetsFilter(event.target.value);
  };

  const [replacementActiveSetFilter, setReplacementActiveSetFilter] = useState<string>('');
  const updateActiveReplacementSetFilter = (event: ChangeEvent<HTMLInputElement>) => {
    setReplacementActiveSetFilter(event.target.value);
  };

  const {
    replacementSourceText,
    replacementSets,
    activeReplacementSet,
    originalActiveReplacementSet,
    activeReplacementSetItems,
    originalActiveReplacementSetItems,
    activeReplacementSetShared,
    originalActiveReplacementSetShared,
    newSourceText,
    newTargetText,
  } = useAppSelector((store) => store.app.replacements);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hideReplacementsModal = () => {
    dispatch(
      updateReplacements({
        replacementSourceText: undefined,
        //replacementSets: undefined,
        activeReplacementSet: undefined,
        originalActiveReplacementSet: undefined,
        activeReplacementSetItems: [],
        originalActiveReplacementSetItems: [],
        activeReplacementSetShared: false,
        originalActiveReplacementSetShared: false,
        newSourceText: '',
        newTargetText: '',
      })
    );
    dispatch(
      setReplacementsModal({
        visible: false,
      })
    );
  };

  const changeToCreateSetType = () => {
    dispatch(setReplacementsModal({ type: ReplacementsModalType.create_set }));
    dispatch(
      updateReplacements({
        activeReplacementSet: { id: -1, name: '', type: ReplacementSetType.PERSONAL, isOwner: false },
        //replacementSets: undefined
      })
    );
  };
  const changeToEditSetType = (set: IReplacementSet) => {
    if (!set.isOwner) return;
    dispatch(setReplacementsModal({ type: ReplacementsModalType.edit_set }));
    dispatch(
      updateReplacements({
        //replacementSets: [],
        activeReplacementSet: set,
        originalActiveReplacementSet: set,
      })
    );
  };
  const changeToDeleteSetType = (set: IReplacementSet) => {
    dispatch(setReplacementsModal({ type: ReplacementsModalType.delete_set }));
    dispatch(
      updateReplacements({
        //replacementSets: undefined,
        activeReplacementSet: set,
      })
    );
  };
  const changeToGroupsType = () => {
    dispatch(setReplacementsModal({ type: ReplacementsModalType.sets }));
    dispatch(
      updateReplacements({
        activeReplacementSet: undefined,
        originalActiveReplacementSet: undefined,
        activeReplacementSetItems: [],
        originalActiveReplacementSetItems: [],
        activeReplacementSetShared: false,
        originalActiveReplacementSetShared: false,
        newSourceText: '',
        newTargetText: '',
      })
    );
  };

  const handleActiveReplacementSetTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeReplacementSet) return;
    if (event.target.value.length === 255) {
      enqueueSnackbar('Maksimalna dolžina imena je 255 znakov.', { variant: 'error' });
      return;
    }
    dispatch(updateReplacements({ activeReplacementSet: { ...activeReplacementSet, name: event.target.value } }));
  };

  const toggleActiveReplacementSetIsShared = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateReplacements({ activeReplacementSetShared: event.target.checked }));
  };

  const handleNewSourceText = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateReplacements({ newSourceText: event.target.value }));
  };
  const handleNewTargetText = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateReplacements({ newTargetText: event.target.value }));
  };

  const hasReplacementSetItemsChanged = (): boolean => {
    // Quick checks
    if (activeReplacementSetItems.length !== originalActiveReplacementSetItems.length) return true;

    for (let i = 0; i < activeReplacementSetItems.length; i++) {
      const itemA = activeReplacementSetItems[i];
      const itemB = originalActiveReplacementSetItems[i];

      if (itemA.id !== itemB.id) return true;

      if (!compareEntryValues(itemA.source, itemB.source)) return true;
      if (!compareEntryValue(itemA.target, itemB.target)) return true;
    }

    if (
      originalActiveReplacementSet &&
      activeReplacementSet &&
      originalActiveReplacementSet.name !== activeReplacementSet.name
    )
      return true;

    if (originalActiveReplacementSetShared !== activeReplacementSetShared) return true;

    return false; // arrays are deeply equal
  };

  const compareEntryValues = (a: IEntryValue[], b: IEntryValue[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!compareEntryValue(a[i], b[i])) return false;
    }
    return true;
  };

  const compareEntryValue = (a: IEntryValue, b: IEntryValue): boolean => {
    return a.text === b.text && a.spaceBefore === b.spaceBefore;
  };

  const convertSourceStringToTokens = (sourceString: string) => {
    const punctuation = /[!?,';:()\-]/;
    const source: IEntryValue[] = [];

    const sourceSplitBySpaces = sourceString.split(' ');

    sourceSplitBySpaces.forEach((token, index) => {
      const isLastToken = sourceSplitBySpaces.length - 1 === index;

      if (token.endsWith('.') && isLastToken && token.length > 1) {
        const main = token.slice(0, -1);
        source.push({
          text: main,
          spaceBefore: index === 0 ? undefined : true,
        });
        source.push({
          text: '.',
          spaceBefore: false,
        });
      } else {
        const chars = token.split('');
        let buffer = '';

        chars.forEach((char, i) => {
          if (punctuation.test(char)) {
            if (buffer) {
              source.push({
                text: buffer,
                spaceBefore: index === 0 ? undefined : source.length > 0 || buffer !== '',
              });
              buffer = '';
            }
            source.push({
              text: char,
              spaceBefore: false,
            });
          } else {
            buffer += char;
          }
        });

        if (buffer) {
          source.push({
            text: buffer,
            spaceBefore: index === 0 ? undefined : source.length === 0 || source[source.length - 1].text !== '',
          });
        }
      }
    });

    return source;
  };
  const addReplacementItemToSet = () => {
    let alreadyExist = false;
    activeReplacementSetItems.forEach((replacementSetItem) => {
      const replacementSetItemSourceText = replacementSetItem.source
        .map((source) => `${source.spaceBefore ? ' ' : ''}${source.text}`)
        .join('');
      if (replacementSetItemSourceText === newSourceText) {
        alreadyExist = true;
      }
    });

    if (alreadyExist) {
      enqueueSnackbar(`Zamenjava za "${newSourceText}" že obstaja`, { variant: 'error' });
      return;
    }
    const copy = cloneDeep(activeReplacementSetItems);
    const source = convertSourceStringToTokens(newSourceText);
    copy.unshift({
      id: -DateTime.now().toMillis(),
      target: { spaceBefore: undefined, text: newTargetText },
      source,
    });
    dispatch(
      updateReplacements({
        activeReplacementSetItems: copy,
        newSourceText: '',
        newTargetText: '',
      })
    );
  };

  const removeReplacementItemFromSet = (index: number) => {
    const copy = cloneDeep(activeReplacementSetItems);
    copy.splice(index, 1);
    dispatch(updateReplacements({ activeReplacementSetItems: copy }));
  };

  const updateSetItemSource = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const copy = cloneDeep(activeReplacementSetItems);
    const source = convertSourceStringToTokens(event.target.value);
    copy[index].source = source;
    dispatch(updateReplacements({ activeReplacementSetItems: copy }));
  };
  const updateSetItemTarget = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const copy = cloneDeep(activeReplacementSetItems);
    copy[index].target.text = event.target.value;
    dispatch(updateReplacements({ activeReplacementSetItems: copy }));
  };

  const createNewSet = async () => {
    if (!activeReplacementSet) return;

    try {
      const response = await createReplacementSet({
        name: activeReplacementSet.name.replaceAll(' ', '-'),
        isShared: activeReplacementSetShared,
      });
      const setId = response.data.id;
      await createReplacementSetItems({ id: setId, items: activeReplacementSetItems });
      //This means that replacements where opened from editor
      //in this case modal should be hidden after editor process current source as replacement and not here.
      //somehow existing function in editor should be triggered so that replacement is visible in existing editor and after processing aswell.

      if (replacementSourceText) {
        dispatch(updateReplacements({ triggerEditorReplacement: true }));
        return;
      }

      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  const arraysAreEqual = (a: IEntryValue[], b: IEntryValue[]): boolean => {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!objectsAreEqual(a[i], b[i])) return false;
    }
    return true;
  };

  const objectsAreEqual = (a: IEntryValue, b: IEntryValue): boolean =>
    a.text === b.text && a.spaceBefore === b.spaceBefore;

  const updateExistingSet = async () => {
    if (!activeReplacementSet) return;

    try {
      const setId = activeReplacementSet.id;
      const originalItemIds = originalActiveReplacementSetItems.map((item) => item.id);
      const currentItemIds = activeReplacementSetItems.map((item) => item.id);

      const deleteItemIds = originalItemIds.filter((id) => !currentItemIds.includes(id));
      const addItemIds = currentItemIds.filter((id) => !originalItemIds.includes(id));
      const commonItemIds = originalItemIds.filter((id) => currentItemIds.includes(id));

      const elementsToAdd = activeReplacementSetItems.filter((item) => addItemIds.includes(item.id));
      const changedItems = commonItemIds
        .map((id) => {
          const originalItem = originalActiveReplacementSetItems.find((item) => item.id === id);
          const currentItem = activeReplacementSetItems.find((item) => item.id === id);

          if (originalItem && currentItem) {
            const sourceChanged = !arraysAreEqual(originalItem.source, currentItem.source);
            const targetChanged = !objectsAreEqual(originalItem.target, currentItem.target);

            if (sourceChanged || targetChanged) {
              return currentItem;
            }
          }
          return null;
        })
        .filter((item) => item !== null);

      //await updateReplacementSetItems({id: setId, toCreateItems: elementsToAdd, toUpdateItems: changedItems, toDeleteIndexes: deleteItemIds});
      const requests = updateReplacementSetItems({
        id: setId,
        toCreateItems: elementsToAdd,
        toUpdateItems: changedItems,
        toDeleteIndexes: deleteItemIds,
      });

      await patchReplacementSet({
        id: setId,
        isShared: activeReplacementSetShared,
        name: originalActiveReplacementSet?.name === activeReplacementSet.name ? undefined : activeReplacementSet.name,
      });

      for (let requestIndex = 0; requestIndex < requests.length; requestIndex++) {
        const request = requests[requestIndex];
        await request();
      }

      if (replacementSourceText) {
        dispatch(updateReplacements({ triggerEditorReplacement: true }));
        return;
      }

      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteSet = async () => {
    if (!activeReplacementSet) return;
    try {
      await deleteReplacementSet(activeReplacementSet.id);
      changeToGroupsType();
    } catch (error) {
      console.log(error);
    }
  };

  const fetchReplacementSets = async () => {
    try {
      const replacementsResponse = await getReplacementSets();

      const localReplacements: LocalReplacementSets = replacementSets
        ? replacementsResponse.data.map((set) => {
            const match = replacementSets.find((prev) => prev.set.id === set.id);
            return { checked: match?.checked ?? false, set };
          })
        : replacementsResponse.data.map((set) => {
            return { checked: false, set };
          });

      localReplacements.sort((a, b) => priorities[a.set.type] - priorities[b.set.type]);

      localStorage.setItem('replacement_sets', JSON.stringify(localReplacements));

      dispatch(updateReplacements({ replacementSets: localReplacements }));
    } catch (error) {
      enqueueSnackbar('Pri pridobivanju skupin zamenjav je prišlo do napake.', { variant: 'error' });
    }
  };

  const fetchReplacementSet = async () => {
    if (!activeReplacementSet) return;

    try {
      //const newSet = getReplacementSet(activeReplacementSet.id)
      const replacementSetItems = await getReplacementSetItems(activeReplacementSet.id);
      dispatch(
        updateReplacements({
          activeReplacementSetShared:
            activeReplacementSet.type !== ReplacementSetType.PERSONAL && activeReplacementSet.isOwner,
          originalActiveReplacementSetShared:
            activeReplacementSet.type !== ReplacementSetType.PERSONAL && activeReplacementSet.isOwner,
          activeReplacementSetItems: replacementSetItems.data,
          originalActiveReplacementSetItems: replacementSetItems.data,
        })
      );
    } catch (error) {
      changeToGroupsType();
    }
  };

  const replacementSetItemsDiferent = hasReplacementSetItemsChanged();

  const handleCsvUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const processCSV = (text: string) => {
    const rows: string[][] = [];
    let currentRow: string[] = [];
    let currentValue = '';
    let insideQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (insideQuotes) {
        if (char === '"' && nextChar === '"') {
          currentValue += '"'; // Handle escaped quote
          i++;
        } else if (char === '"') {
          insideQuotes = false;
        } else {
          currentValue += char;
        }
      } else {
        if (char === '"') {
          insideQuotes = true;
        } else if (char === ',') {
          currentRow.push(currentValue);
          currentValue = '';
        } else if (char === '\r' && nextChar === '\n') {
          currentRow.push(currentValue);
          rows.push(currentRow);
          currentRow = [];
          currentValue = '';
          i++; // Skip \n
        } else if (char === '\n' || char === '\r') {
          currentRow.push(currentValue);
          rows.push(currentRow);
          currentRow = [];
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
    }

    // Push the last value/row if needed
    if (currentValue !== '' || currentRow.length > 0) {
      currentRow.push(currentValue);
      rows.push(currentRow);
    }

    const tmp: ReplacementSetItems = [];
    rows.forEach((row, index) => {
      const sourceText = row[0];
      const targetText = row[1];

      const sourceTokenized = convertSourceStringToTokens(sourceText);
      const target: IEntryValue = { spaceBefore: undefined, text: targetText };

      if (index > 0) {
        tmp.push({ id: DateTime.now().toMillis(), source: sourceTokenized, target });
      }
    });

    dispatch(
      updateReplacements({
        activeReplacementSetItems: activeReplacementSetItems ? [...activeReplacementSetItems, ...tmp] : tmp,
      })
    );
    return rows;
  };
  const handleCsvReplacementsImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    if (file === null) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      processCSV(text);
      event.target.value = '';
    };
    reader.readAsText(file);
  };

  const toggleReplacementSet = (index: number) => {
    if (!replacementSets) return;
    if (replacementSets.length <= index) return;

    const copy = cloneDeep(replacementSets);

    copy[index].checked = !copy[index].checked;

    localStorage.setItem('replacement_sets', JSON.stringify(copy));

    dispatch(updateReplacements({ replacementSets: copy }));
  };

  useEffect(() => {
    if (replacementSourceText) dispatch(updateReplacements({ newSourceText: replacementSourceText }));

    if (replacementsModalVisible && replacementModalType === ReplacementsModalType.sets) {
      setTimeout(() => fetchReplacementSets(), 250);
    } else if (replacementsModalVisible && replacementModalType === ReplacementsModalType.edit_set) {
      setTimeout(() => fetchReplacementSet(), 250);
    }
  }, [replacementModalType, replacementsModalVisible, replacementSourceText]);

  return (
    <Modal visible={replacementsModalVisible} closeModal={hideReplacementsModal} title="Zamenjave">
      <>
        {(replacementModalType === ReplacementsModalType.sets && (
          <div id="replacement_modal_content_container">
            <button onClick={changeToCreateSetType}>
              <div id="create_group_button_container">
                <Add sx={{ color: '#31a0ff' }} />
                <p>Ustvari skupino</p>
              </div>
            </button>

            <div id="replacement_filter_container">
              <Search />
              <input
                id="replacement_search_input"
                placeholder="Išči..."
                value={replacementSetsFilter}
                onChange={updateReplacementSetFilter}
              />
            </div>

            {(replacementSets && (
              <div id="replacements_container">
                {replacementSets
                  .filter((replacementSet) => replacementSet.set.name.includes(replacementSetsFilter))
                  .filter((replacementSet) => replacementSet.set.type !== ReplacementSetType.GLOBAL)
                  .map((replacementSet, index) => {
                    const { checked, set } = replacementSet;
                    return (
                      <div className="replacement_wrapper" id={set.id.toString()}>
                        <div className="replacement_name_wrapper">
                          <input type="checkbox" checked={checked} onChange={() => toggleReplacementSet(index)} />
                          <Tooltip
                            disableHoverListener={!set.isOwner}
                            disableFocusListener={!set.isOwner}
                            disableTouchListener={!set.isOwner}
                            title="Uredi skupino"
                            arrow
                            disableInteractive
                            placement="left"
                          >
                            <button className="replacement_name_wrapper" onClick={() => changeToEditSetType(set)}>
                              {(set.type === ReplacementSetType.PERSONAL && <PersonIcon />) ||
                                (set.type === ReplacementSetType.SHARED && <GroupsIcon />) ||
                                (set.type === ReplacementSetType.GLOBAL && <PublicIcon />)}
                              <p>{set.name}</p>
                            </button>
                          </Tooltip>
                        </div>
                        {set.isOwner && (
                          <Tooltip arrow disableInteractive placement="right" title="Izbriši skupino">
                            <button className="delete_button_container" onClick={() => changeToDeleteSetType(set)}>
                              <Delete />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    );
                  })}
              </div>
            )) || (
              <div id="replacements_loader_container">
                <span id="replacements_loader" />
              </div>
            )}
          </div>
        )) ||
          (replacementModalType === ReplacementsModalType.create_set && activeReplacementSet && (
            <div id="replacement_modal_content_container">
              <div id="create_replacement_name_input_container">
                <input
                  maxLength={255}
                  id="create_replacement_name_input"
                  placeholder="Vnesi ime skupine"
                  value={activeReplacementSet.name}
                  onChange={handleActiveReplacementSetTitle}
                />
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <p>Deli: </p>
                  <input
                    type="checkbox"
                    checked={activeReplacementSetShared}
                    id="create_replacement_is_shared_input"
                    onChange={toggleActiveReplacementSetIsShared}
                  />
                </div>
              </div>

              <div id="replacement_filter_container">
                <Search />
                <input
                  id="replacement_search_input"
                  placeholder="Išči..."
                  value={replacementActiveSetFilter}
                  onChange={updateActiveReplacementSetFilter}
                />
              </div>

              <div id="replacement_table_container">
                <table className="replacement_table">
                  <thead className="replacement_table_head">
                    <tr className="replacement_table_head_tr">
                      <th className="replacemenet_table_head_th">Iz teksta</th>
                      <th className="replacemenet_table_head_th">V tekst</th>
                      <th className="replacemenet_table_head_th">Akcije</th>
                    </tr>
                  </thead>
                  <tbody className="replacement_table_body">
                    <tr className="replacement_table_tr">
                      <td className="replacement_table_td">
                        <input placeholder="Vnesi besedilo" value={newSourceText} onChange={handleNewSourceText} />
                      </td>
                      <td className="replacement_table_td">
                        <input placeholder="Vnesi besedilo" value={newTargetText} onChange={handleNewTargetText} />
                      </td>
                      <td className="replacement_table_td">
                        <Tooltip arrow disableInteractive placement="right" title="Dodaj zamenjavo">
                          <button
                            onClick={addReplacementItemToSet}
                            style={{ width: '100%', height: '100%', placeContent: 'center' }}
                          >
                            <Add />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                    {activeReplacementSetItems
                      .filter((item) => item.target.text.includes(replacementActiveSetFilter))
                      .map((item, index) => {
                        let sourceText = '';
                        item.source.forEach((sourceItem) => {
                          if (sourceItem.spaceBefore) {
                            sourceText += ' ';
                          }
                          sourceText += sourceItem.text;
                        });

                        return (
                          <tr key={index} className="replacement_table_tr">
                            <td className="replacement_table_td">
                              <input
                                placeholder="Vnesi besedilo"
                                value={sourceText}
                                onChange={(event) => updateSetItemSource(event, index)}
                              />
                            </td>
                            <td className="replacement_table_td">
                              <input
                                placeholder="Vnesi besedilo"
                                value={item.target.spaceBefore ? ' ' : '' + item.target.text}
                                onChange={(event) => updateSetItemTarget(event, index)}
                              />
                            </td>
                            <td className="replacement_table_td">
                              <Tooltip arrow disableInteractive placement="right" title="Izbriši zamenjavo">
                                <button
                                  onClick={() => removeReplacementItemFromSet(index)}
                                  style={{ width: '100%', height: '100%', placeContent: 'center' }}
                                >
                                  <Delete className="delete_button_entry" />
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              <Tooltip
                title={
                  <>
                    <Typography align="left">Pričakovani format .csv datoteke</Typography>
                    <Typography align="left">sourceText,targetText</Typography>
                    <Typography align="left">"Fakulteta za računalništvo in informatiko","FRI"</Typography>
                    <Typography align="left">"Liga prvakov,"LP"</Typography>
                  </>
                }
              >
                <button style={{ marginTop: 24 }} onClick={handleCsvUploadClick}>
                  <div id="create_group_button_container">
                    <UploadFile sx={{ color: '#31a0ff' }} />
                    <p>NALOŽI</p>
                  </div>
                </button>
              </Tooltip>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvReplacementsImport}
                style={{ display: 'none' }}
              />

              <div id="replacements_modal_footer">
                <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                  NAZAJ
                </button>
                <button
                  disabled={activeReplacementSetItems.length < 1 || activeReplacementSet.name.length === 0}
                  id="replacements_modal_save_button"
                  onClick={createNewSet}
                >
                  USTVARI
                </button>
              </div>
            </div>
          )) ||
          (replacementModalType === ReplacementsModalType.edit_set && activeReplacementSet && (
            <div id="replacement_modal_content_container">
              <div id="create_replacement_name_input_container">
                <input
                  maxLength={255}
                  id="create_replacement_name_input"
                  placeholder="Vnesi ime skupine"
                  value={activeReplacementSet.name}
                  onChange={handleActiveReplacementSetTitle}
                />
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <p>Deli: </p>
                  <input
                    type="checkbox"
                    checked={activeReplacementSetShared}
                    id="create_replacement_is_shared_input"
                    onChange={toggleActiveReplacementSetIsShared}
                  />
                </div>
              </div>

              <div id="replacement_filter_container">
                <Search />
                <input
                  id="replacement_search_input"
                  placeholder="Išči..."
                  value={replacementActiveSetFilter}
                  onChange={updateActiveReplacementSetFilter}
                />
              </div>

              <div id="replacement_table_container">
                <table className="replacement_table">
                  <thead className="replacement_table_head">
                    <tr className="replacement_table_head_tr">
                      <th className="replacemenet_table_head_th">Iz teksta</th>
                      <th className="replacemenet_table_head_th">V tekst</th>
                      <th className="replacemenet_table_head_th">Akcije</th>
                    </tr>
                  </thead>
                  <tbody className="replacement_table_body">
                    <tr className="replacement_table_tr">
                      <td className="replacement_table_td">
                        <input placeholder="Vnesi besedilo" value={newSourceText} onChange={handleNewSourceText} />
                      </td>
                      <td className="replacement_table_td">
                        <input placeholder="Vnesi besedilo" value={newTargetText} onChange={handleNewTargetText} />
                      </td>
                      <td className="replacement_table_td">
                        <Tooltip arrow disableInteractive placement="right" title="Dodaj zamenjavo">
                          <button
                            onClick={addReplacementItemToSet}
                            style={{ width: '100%', height: '100%', placeContent: 'center' }}
                          >
                            <Add />
                          </button>
                        </Tooltip>
                      </td>
                    </tr>
                    {activeReplacementSetItems
                      .filter((item) => item.target.text.includes(replacementActiveSetFilter))
                      .map((item, index) => {
                        let sourceText = '';
                        item.source.forEach((sourceItem) => {
                          if (sourceItem.spaceBefore) {
                            sourceText += ' ';
                          }
                          sourceText += sourceItem.text;
                        });

                        return (
                          <tr key={index} className="replacement_table_tr">
                            <td className="replacement_table_td">
                              <input
                                placeholder="Vnesi besedilo"
                                value={sourceText}
                                onChange={(event) => updateSetItemSource(event, index)}
                              />
                            </td>
                            <td className="replacement_table_td">
                              <input
                                placeholder="Vnesi besedilo"
                                value={item.target.spaceBefore ? ' ' : '' + item.target.text}
                                onChange={(event) => updateSetItemTarget(event, index)}
                              />
                            </td>
                            <td className="replacement_table_td">
                              <Tooltip arrow disableInteractive placement="right" title="Izbriši zamenjavo">
                                <button
                                  style={{ width: '100%', height: '100%', placeContent: 'center' }}
                                  onClick={() => removeReplacementItemFromSet(index)}
                                >
                                  <Delete className="delete_button_entry" />
                                </button>
                              </Tooltip>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>

              <Tooltip
                title={
                  <>
                    <Typography align="left">Pričakovani format .csv datoteke</Typography>
                    <Typography align="left">sourceText,targetText</Typography>
                    <Typography align="left">"Fakulteta za računalništvo in informatiko","FRI"</Typography>
                    <Typography align="left">"Liga prvakov,"LP"</Typography>
                  </>
                }
              >
                <button style={{ marginTop: 24 }} onClick={handleCsvUploadClick}>
                  <div id="create_group_button_container">
                    <UploadFile sx={{ color: '#31a0ff' }} />
                    <p>NALOŽI</p>
                  </div>
                </button>
              </Tooltip>

              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvReplacementsImport}
                style={{ display: 'none' }}
              />

              <div id="replacements_modal_footer">
                <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                  NAZAJ
                </button>
                <button
                  disabled={!replacementSetItemsDiferent || activeReplacementSet.name.length === 0}
                  id="replacements_modal_save_button"
                  onClick={updateExistingSet}
                >
                  SHRANI
                </button>
              </div>
            </div>
          )) ||
          (replacementModalType === ReplacementsModalType.delete_set && activeReplacementSet && (
            <div id="replacement_modal_content_container">
              <p id="replacement_delete_text">
                Ali ste prepričani, da želite izbrisati skupino <strong>{activeReplacementSet.name}</strong>?
              </p>
              <div id="replacements_modal_footer">
                <button id="replacements_modal_close_button" onClick={changeToGroupsType}>
                  NAZAJ
                </button>
                <button id="replacements_modal_delete_button" onClick={deleteSet}>
                  IZBRIŠI
                </button>
              </div>
            </div>
          ))}
      </>
    </Modal>
  );
};

export default ReplacementsModal;
